<template>
  <q-dialog
    ref="dialog"
    data-e2e="dialog-edit-authorized-service"
  >
    <q-card
      class="dialog-card"
    >
      <q-card-section class="row">
        <div
          v-if="isAddingAuthorization"
          class="text-h6 ellipsis col"
        >
          {{ $t('authorizations.editAuthorization.addTitle') }}
        </div>
        <div
          v-else
          class="text-h6 ellipsis col"
        >
          {{ $t('authorizations.editAuthorization.editTitle') }}
        </div>
        <div class="col-auto">
          <q-btn
            v-close-popup
            flat
            round
            dense
            icon="close"
            @click="hide"
          />
        </div>
      </q-card-section>
      <q-form
        ref="form"
        autofocus
        greedy
        @submit="onSubmitForm"
        @validation-error="onValidateTreatmentServices"
      >
        <q-card-section class="row q-col-gutter-sm">
          <q-select
            v-model="editAuthorization.fundingSource"
            data-e2e="dropdown-funding-source"
            :label="$t('authorizations.fields.fundingSource')"
            :options="sortedFundingSources"
            option-value="fundingSourceId"
            option-label="name"
            outlined
            hide-bottom-space
            emit-value
            map-options
            :disable="!isAddingAuthorization"
            :rules="rules.fundingSource"
            class="col-12"
            @update:model-value="onClearTreatmentService"
          />
          <q-input
            v-model="editAuthorization.authorizationNumber"
            data-e2e="input-authorization-number"
            :label="$t('authorizations.fields.authorizationNumber')"
            outlined
            hide-bottom-space
            :rules="rules.authorizationNumber"
            class="col-12"
          />
          <date-input
            v-model="editAuthorization.startTimestamp"
            data-e2e="input-start-date"
            :label="$t('authorizations.fields.startTimestamp')"
            outlined
            hide-bottom-space
            :rules="rules.startTimestamp"
            class="col-6"
            @update:model-value="onSetStartTimestamp"
          />
          <date-input
            ref="endDateInput"
            :model-value="editAuthorization.endTimestamp"
            data-e2e="input-end-date"
            :label="$t('authorizations.fields.endTimestamp')"
            outlined
            hide-bottom-space
            :rules="rules.endTimestamp"
            class="col-6"
            @update:model-value="onSetEndTimestamp"
          />
          <treatment-services-selector
            v-model="editValidTreatmentServices"
            :disable="!isAddingAuthorization || !editAuthorization.fundingSource"
            :hide-add-remove-buttons="!isAddingAuthorization"
            :available-treatment-services="availableTreatmentServices"
            class="col-12"
            @update:model-value="onResetValidTreatmentServices"
          />
          <div class="col-12">
            <q-input
              ref="validTreatmentServicesInput"
              v-model="editValidTreatmentServices"
              :rules="rules.validTreatmentServices"
              class="hidden"
            />
            <div
              v-if="showTreatmentServicesError"
              class="q-my-sm text-negative"
            >
              <div role="alert">
                {{ $t('authorizations.editAuthorization.inputTreatmentServicesError') }}
              </div>
            </div>
          </div>
        </q-card-section>
        <q-card-actions class="row">
          <universal-tooltip
            :show-tooltip="isUsedInAppointments"
            :text="$t('authorizations.editAuthorization.disableButtonDeleteTooltip')"
          >
            <q-btn
              v-if="!isAddingAuthorization"
              flat
              data-e2e="button-delete"
              :label="$t('common.buttonDelete')"
              :loading="isLoadingAppointmentsForDeleteCheck"
              :disable="isUsedInAppointments"
              color="negative"
              @click="onDelete"
            />
          </universal-tooltip>
          <q-space />
          <q-btn
            v-close-popup
            flat
            data-e2e="button-cancel"
            :label="$t('common.buttonCancel')"
            color="secondary"
            @click="hide"
          />
          <q-btn
            flat
            data-e2e="button-save"
            :label="$t('common.buttonSave')"
            type="submit"
            :loading="isLoadingAppointmentsForDateValidation"
            color="primary"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import DateInput from 'components/DateInput.vue';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { Timestamp } from 'firebase/firestore';
import { mapActions, mapState } from 'pinia';
import ServiceAppointmentConflictDialog from 'src/components/Billing/ServiceAppointmentConflictDialog.vue';
import TreatmentServicesSelector from 'src/components/Billing/TreatmentServicesSelector.vue';
import { useScheduling } from 'src/composables/scheduling';
import { useTime } from 'src/composables/time';
import AppointmentsDB from 'src/fb/appointments-db';
import Interactions from 'src/mixins/Interactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'EditAuthorizedServiceDialog',

  components: {
    UniversalTooltip,
    DateInput,
    TreatmentServicesSelector,
  },

  mixins: [Interactions, Toolbox],

  props: {
    authorization: {
      type: Object,
      required: true,
    },
    fundingSources: {
      type: Array,
      required: true,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { getTreatmentServiceCodeAndModifiers } = useScheduling();
    const { getLocaleDateString } = useTime();
    return { getTreatmentServiceCodeAndModifiers, getLocaleDateString };
  },

  data() {
    return {
      showTreatmentServicesError: false,

      editAuthorization: {
        fundingSource: null,
        authorizationNumber: null,
        startTimestamp: null,
        endTimestamp: null,
        treatmentService: null,
      },
      editValidTreatmentServices: [],

      isLoadingAppointmentsForDeleteCheck: false,
      isLoadingAppointmentsForDateValidation: false,
      isUsedInAppointments: false,
      datesChanged: false,

      rules: {
        fundingSource: [
          (val) => !!val || this.$t('authorizations.editAuthorization.inputFundingSourceError'),
        ],
        authorizationNumber: [
          (val) => !!val || this.$t('common.validationRequired'),
        ],
        startTimestamp: [
          (val) => !!val || this.$t('common.validationRequired'),
        ],
        endTimestamp: [
          (val) => !!val || this.$t('common.validationRequired'),
          (val) => (
            this.editAuthorization?.endTimestamp instanceof Timestamp
              && this.editAuthorization?.startTimestamp instanceof Timestamp
              && (this.editAuthorization?.endTimestamp.toMillis() > this.editAuthorization.startTimestamp.toMillis())
          ) || this.$t('authorizations.editAuthorization.inputEndTimestampError'),
        ],
        validTreatmentServices: [
          (val) => (val || []).length > 0
            || this.$t('authorizations.editAuthorization.inputTreatmentServicesError'),
        ],
      },

    };
  },

  computed: {
    ...mapState(auth, ['connectedClinic', 'treatmentServices']),

    isAddingAuthorization() {
      return !this.authorization.id;
    }, // isAddingAuthorization"()

    availableTreatmentServices() {
      // When adding a new authorization, we allow multiple treatment services
      // But only those valid for the selected funding source. We only include the ids
      // because we are not interested in bringing forward any modifications made to
      // the treatment service.
      if (this.isAddingAuthorization) {
        // The payer, and its covered services in the model's vocabulary — the list arrives resolved
        // from the Revenue Cycle context, so `fs.id` (a version document) is not what to match on.
        return this.fundingSources
          .find((fs) => fs.fundingSourceId === this.editAuthorization.fundingSource)?.coveredServices
          ?.map((coveredService) => {
            const treatmentService = this.treatmentServices
              .find(({ id }) => (id === coveredService.treatmentServiceId));

            // If a clinic doesn’t have such a treatment service, it was probably deleted, so it should not be included.
            if (!treatmentService) {
              return null;
            }

            // Take the id explicitly rather than spreading the covered-service entry over the
            // treatment service. The entry is the FUNDER's configuration for this service, and
            // Slice 2 gives it per-service rendering and authorization overrides — spreading it
            // would silently graft those onto the authorization's treatment-service object,
            // where they mean nothing and would be saved back.
            return {
              ...treatmentService,
              id: coveredService.treatmentServiceId,
            };
          }).filter(Boolean) || [];
      }

      return [
        {
          ...this.treatmentServices.find(({ id }) => (id === this.editAuthorization.treatmentService?.id)),
          ...this.editAuthorization.treatmentService,
        },

      ];
    }, // availableTreatmentServices()

    sortedFundingSources() {
      return this.naturalSort(this.fundingSources, 'name');
    }, // sortedFundingSources()

  }, // computed

  created() {
    // Lazily load treatment services (no longer eager-loaded at login).
    // Fire-and-forget: ensureTreatmentServicesBound rejects on a bind error (which it
    // already logs internally), so swallow here to avoid an unhandled rejection.
    this.ensureTreatmentServicesBound().catch(() => {});
    this.editAuthorization = {
      startTimestamp: null,
      endTimestamp: null,
      ...this.authorization,
    };
    if (!this.isAddingAuthorization) {
      this.editValidTreatmentServices = [
        this.editAuthorization.treatmentService,
      ];
    }
  }, // created()

  methods: {
    ...mapActions(auth, ['ensureTreatmentServicesBound']),

    show() {
      this.$refs.dialog.show();
    }, // show()

    hide() {
      this.$emit('hide');

      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    async onSubmitForm() {
      const conflictingAppointments = await this.getConflictingAppointments();

      if (!conflictingAppointments.length) {
        this.$emit('ok', {
          authorization: this.editAuthorization,
          treatmentServices: this.editValidTreatmentServices.map(({ id }) => ({ id })),
        });
        this.hide();
        return;
      }

      const {
        fundingSourceName, startTimestamp, endTimestamp, treatmentServiceName, treatmentServiceCode,
      } = this.editAuthorization;

      const startTime = startTimestamp instanceof Timestamp ? this.getLocaleDateString(startTimestamp) : '';
      const endTime = endTimestamp instanceof Timestamp ? this.getLocaleDateString(endTimestamp) : '';

      const treatmentService = `${fundingSourceName || ''} ${treatmentServiceCode || ''} ${treatmentServiceName || ''} 
          ${startTime} - ${endTime}`;

      this.openComponentDialog(
        ServiceAppointmentConflictDialog,
        {
          treatmentService,
          appointments: conflictingAppointments,
          mode: 'edit',
        },
        async (appointmentsAction) => {
          await this.handleConflictingAppointments(appointmentsAction);
          this.$emit('ok', {
            authorization: this.editAuthorization,
          });
          this.hide();
        },
      );
    }, // onSubmitForm()

    async getAppointmentsUsingAuthorizedService(loadingFlag, limit = null) {
      try {
        this[loadingFlag] = true;
        const appointmentsDb = new AppointmentsDB();
        const appointmentsUsingAuthorizedService = await appointmentsDb.readAll(
          [
            ['clinic', '==', this.editAuthorization.clinic],
            ['client', '==', this.editAuthorization.client],
            ['authorization', '==', this.editAuthorization.id],
            ['status', 'in', ['created', 'draft', 'scheduled']],
          ],
          null,
          null,
          limit,
        );

        if (!appointmentsUsingAuthorizedService.length) {
          return [];
        }

        return appointmentsUsingAuthorizedService;
      } catch (err) {
        this.notifyNegative(err.message);
        console.error(err.message);
        logEvent(err);
      } finally {
        this[loadingFlag] = false;
      }
    }, // getAppointmentsUsingAuthorizedService()

    async getConflictingAppointments() {
      if (!this.editAuthorization.client || this.isAddingAuthorization || !this.datesChanged) {
        return [];
      }

      const appointmentsUsingAuthorizedService = await this.getAppointmentsUsingAuthorizedService(
        'isLoadingAppointmentsForDateValidation',
      );

      const { startTimestamp, endTimestamp } = this.editAuthorization;

      return appointmentsUsingAuthorizedService.filter((appointment) => {
        if (!(appointment.scheduledStartTimestamp instanceof Timestamp) || !(startTimestamp instanceof Timestamp)
            || !(endTimestamp instanceof Timestamp)) {
          return false;
        }

        const appointmentStart = appointment.scheduledStartTimestamp.toMillis();
        if (appointmentStart < startTimestamp.toMillis()) {
          return true;
        }

        const appointmentEnd = appointmentStart + appointment.duration * 1000;
        return appointmentEnd > endTimestamp.toMillis();
      });
    }, // getConflictingAppointments()

    async handleConflictingAppointments(action) {
      try {
        this.isLoadingAppointmentsForDateValidation = true;
        await callFirebaseFunction('handleConflictingAppointments', {
          action,
          clinicId: this.connectedClinic.id,
          clientId: this.editAuthorization.client,
          authorization: {
            id: this.editAuthorization.id,
            authStartTimeMillis: this.editAuthorization.startTimestamp.toMillis(),
            authEndTimeMillis: this.editAuthorization.endTimestamp.toMillis(),
          },
        });
        this.notifySuccess(this.$t('authorizations.editAuthorization.notifyUpdateAppointmentsSuccess'));
      } catch (err) {
        this.notifyNegative(err.message);
        console.error(err.message);
        logEvent(err);
      } finally {
        this.isLoadingAppointmentsForDateValidation = false;
      }
    }, // handleConflictingAppointments()

    onResetValidTreatmentServices() {
      this.showTreatmentServicesError = false;
    }, // onResetValidTreatmentServices()

    onValidateTreatmentServices() {
      this.showTreatmentServicesError = !this.$refs.validTreatmentServicesInput.validate();
    }, // onValidateTreatmentServices()

    async isAuthorizedServiceInUse() {
      const appointmentsUsingAuthorizedService = await this.getAppointmentsUsingAuthorizedService(
        'isLoadingAppointmentsForDeleteCheck',
        1,
      );

      return appointmentsUsingAuthorizedService.length > 0;
    },

    async onDelete() {
      const isAuthorizedServiceInUse = await this.isAuthorizedServiceInUse();

      if (isAuthorizedServiceInUse) {
        this.notifyWarning(this.$t('authorizations.editAuthorization.disableButtonDeleteTooltip'));
        this.isUsedInAppointments = true;
        return;
      }

      this.confirmDialog(this.$t('common.dialogTitleConfirm'), this.$t('common.notifyDeleteConfirm'), () => {
        this.$emit('ok', {
          authorization: { ...this.editAuthorization, editStatus: 'delete' },
        });
        this.hide();
      });
    }, // onDelete()

    onClearTreatmentService() {
      this.editValidTreatmentServices = [];
    }, // onClearTreatmentService()

    onSetStartTimestamp(startTimestamp) {
      if (!(startTimestamp instanceof Timestamp)) {
        this.editAuthorization.startTimestamp = null;
        return;
      }
      this.editAuthorization.startTimestamp = startTimestamp;
      this.datesChanged = true;

      // The end date's rule compares against the start date, so moving the start can turn its error
      // on or off — and the field cannot notice that on its own, because its own value did not change.
      //
      // Only that field, and only once it holds a date. This used to validate the WHOLE form, which
      // painted "required" on every field the user had not reached yet and tripped the form's
      // validation-error handler into showing the treatment-services error, all from picking a start
      // date. An empty end date has nothing to compare against; the required rule covers it on submit.
      if (this.editAuthorization.endTimestamp instanceof Timestamp) {
        this.$refs.endDateInput.validate();
      }
    }, // onSetStartTimestamp()

    onSetEndTimestamp(endTimestamp) {
      if (!(endTimestamp instanceof Timestamp)) {
        this.editAuthorization.endTimestamp = null;
        return;
      }
      // Set timestamp to 23:59:59 of the day
      this.editAuthorization.endTimestamp = Timestamp.fromDate(
        new Date(endTimestamp.toMillis() + (24 * 60 * 60 * 1000) - 1),
      );
      this.datesChanged = true;
    }, // onSetEndTimestamp()

  },
};
</script>
<style lang="scss" scoped>
.dialog-card {
  width: 500px;
}
</style>
